import type { WindowPosition, WindowSize } from './types';

/**
 * Height (px) of the TopMenuBar + its environment-colour border in EasyMap.
 * Explorers must not slide under it. Kept here so the layout invariant has
 * exactly one home; EasyMap.scss's `.map-container { top: 50px }` is the
 * same number.
 */
export const MAP_TOP_OFFSET = 50;

export interface Viewport {
  width: number;
  height: number;
}

export interface ViewportInsets {
  /** Pixels reserved at the top of the viewport (e.g. TopMenuBar height). */
  top?: number;
  /** Pixels reserved at the bottom (e.g. status bar). */
  bottom?: number;
  /** Pixels reserved on the left. */
  left?: number;
  /** Pixels reserved on the right. */
  right?: number;
}

export interface ClampResult {
  position: WindowPosition;
  size: WindowSize;
}

/**
 * Clamp a draggable window so it always fits inside the viewport with the
 * given margin on every edge.
 *
 * Rules, applied in order:
 *  1. Size is shrunk so width ≤ viewport.width - 2*margin and likewise for height.
 *  2. Position is moved into [margin, viewport.* - size.* - margin] so that
 *     - the title bar stays clickable (position ≥ margin) and
 *     - the bottom-right resize handle stays on-screen.
 *
 * Wrappers can pass any "preferred" size they like; this function keeps them
 * honest on small displays (e.g. an M2 MacBook Air at ~1470×956 px). Pure
 * function, no side effects — safe to call on every render or window resize.
 */
export function clampWindowToViewport(
  position: WindowPosition,
  size: WindowSize,
  viewport: Viewport,
  margin = 20,
  insets: ViewportInsets = {},
): ClampResult {
  const top = insets.top ?? 0;
  const bottom = insets.bottom ?? 0;
  const left = insets.left ?? 0;
  const right = insets.right ?? 0;

  // Usable area = viewport minus insets minus per-edge margin.
  const minX = left + margin;
  const minY = top + margin;
  const maxRight = viewport.width - right - margin;
  const maxBottom = viewport.height - bottom - margin;

  const maxWidth = Math.max(100, maxRight - minX);
  const maxHeight = Math.max(100, maxBottom - minY);

  const clampedSize: WindowSize = {
    width: Math.min(size.width, maxWidth),
    height: Math.min(size.height, maxHeight),
  };

  const maxX = maxRight - clampedSize.width;
  const maxY = maxBottom - clampedSize.height;

  const clampedPosition: WindowPosition = {
    x: Math.min(Math.max(position.x, minX), maxX),
    y: Math.min(Math.max(position.y, minY), maxY),
  };

  return { position: clampedPosition, size: clampedSize };
}
